/**
 * Halaman Publik Download Rote Smart Service
 * Halaman publik untuk mendownload aplikasi Android Rote Smart Service
 * Dapat diakses tanpa login dengan link: DOMAIN_ESA_OFFICE/download/rote-smart-service
 */

import { useState, useEffect } from "react";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React themes
import theme from "assets/theme";

// Icons
import Icon from "@mui/material/Icon";
import DownloadIcon from "@mui/icons-material/Download";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import SecurityIcon from "@mui/icons-material/Security";

// Images
import esaOfficeBanner from "assets/images/eo_cover.png";
import eo_mini1 from "assets/images/eo_mini1.png";

// Link download APK
const APK_DOWNLOAD_URL =
  "https://esa-office.rotendaokab.go.id/RSS/Rote-Smart-Service.apk";

function DownloadRSS() {
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Deteksi ukuran layar untuk mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 960);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Pilih banner berdasarkan ukuran layar
  const bannerImage = isMobile ? eo_mini1 : esaOfficeBanner;

  const handleDownload = () => {
    setDownloading(true);
    setDownloadProgress(0);

    // Menggunakan XMLHttpRequest untuk tracking progress
    const xhr = new XMLHttpRequest();
    xhr.open("GET", APK_DOWNLOAD_URL, true);
    xhr.responseType = "blob";

    // Track progress
    xhr.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        setDownloadProgress(percentComplete);
      }
    };

    // Handle completion
    xhr.onload = () => {
      if (xhr.status === 200) {
        // Buat blob URL dan trigger download
        const blob = xhr.response;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "Rote-Smart-Service.apk";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Cleanup blob URL
        window.URL.revokeObjectURL(url);

        setDownloadProgress(100);
        setDownloadComplete(true);

        // Reset state setelah beberapa detik
        setTimeout(() => {
          setDownloading(false);
          setDownloadProgress(0);
          setDownloadComplete(false);
        }, 5000);
      } else {
        // Error handling
        setDownloading(false);
        setDownloadProgress(0);
        alert("Gagal mengunduh file. Silakan coba lagi.");
      }
    };

    // Handle error
    xhr.onerror = () => {
      setDownloading(false);
      setDownloadProgress(0);
      alert("Terjadi kesalahan saat mengunduh file. Silakan coba lagi.");
    };

    // Start download
    xhr.send();
  };

  const langkahInstalasi = [
    {
      step: 1,
      title: "Download Aplikasi",
      description:
        "Klik tombol 'Download Aplikasi' di bawah ini untuk mengunduh file APK.",
    },
    {
      step: 2,
      title: "Izinkan Instalasi dari Sumber Tidak Dikenal",
      description:
        "Buka Pengaturan > Keamanan > Aktifkan 'Izinkan instalasi dari sumber tidak dikenal' atau 'Unknown Sources'.",
    },
    {
      step: 3,
      title: "Buka File APK",
      description:
        "Buka file manager di ponsel Anda dan cari file 'Rote-Smart-Service.apk' yang telah diunduh.",
    },
    {
      step: 4,
      title: "Instal Aplikasi",
      description:
        "Ketuk file APK dan ikuti instruksi instalasi yang muncul di layar.",
    },
    {
      step: 5,
      title: "Selesai",
      description:
        "Setelah instalasi selesai, aplikasi Rote Smart Service siap digunakan.",
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Card>
          {/* Banner Header */}
          <MDBox
            sx={{
              position: "relative",
              width: "100%",
              height: "200px",
              overflow: "hidden",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <img
              src={bannerImage}
              alt="ESA Office Banner"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </MDBox>

          <CardContent>
            {/* Judul */}
            <MDBox textAlign="center" mb={4}>
              <MDTypography variant="h4" color="text" fontWeight="bold" mb={1}>
                Rote Smart Service
              </MDTypography>
              <MDTypography variant="h6" color="text.secondary" mb={2}>
                Aplikasi Resmi Pemerintah Kabupaten Rote Ndao
              </MDTypography>
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={1}
                mb={2}
              >
                <SecurityIcon color="success" />
                <MDTypography
                  variant="body2"
                  color="success.main"
                  fontWeight="medium"
                >
                  Link Resmi Aplikasi Rote Smart Service
                </MDTypography>
              </MDBox>
            </MDBox>

            <Divider sx={{ my: 3 }} />

            {/* Informasi Update Terbaru */}
            <MDBox
              p={3}
              borderRadius={2}
              bgcolor="success.lighter"
              border="1px solid"
              borderColor="success.main"
              mb={4}
            >
              <MDBox display="flex" alignItems="flex-start" gap={2} mb={3}>
                <Icon color="success" sx={{ mt: 0.5, fontSize: "1.5rem" }}>
                  update
                </Icon>
                <MDBox flex={1}>
                  <MDTypography
                    variant="h6"
                    color="text"
                    fontWeight="bold"
                    mb={1}
                  >
                    Update Terbaru
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary" mb={1}>
                    Versi terbaru aplikasi Rote Smart Service telah tersedia.
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    <strong>Tanggal Pembuatan:</strong> 11 Januari 2026
                  </MDTypography>
                </MDBox>
              </MDBox>

              {/* Tombol Download */}
              <MDBox display="flex" justifyContent="center" mt={2}>
                <MDButton
                  variant="gradient"
                  color="success"
                  size="large"
                  onClick={handleDownload}
                  disabled={downloading}
                  startIcon={
                    downloading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {downloading ? "Mengunduh..." : "Download Aplikasi"}
                </MDButton>
              </MDBox>

              {/* Progress Bar */}
              {downloading && (
                <MDBox mt={3}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <MDTypography
                      variant="body2"
                      color="text"
                      fontWeight="medium"
                    >
                      Progress Download
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      color="text.secondary"
                      fontWeight="bold"
                    >
                      {downloadProgress}%
                    </MDTypography>
                  </MDBox>
                  <MDProgress
                    value={downloadProgress}
                    color="success"
                    variant="gradient"
                    sx={{ height: 8, borderRadius: 2 }}
                  />
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    mt={0.5}
                    display="block"
                  >
                    {downloadProgress < 100
                      ? "Mengunduh file, harap tunggu..."
                      : "Download selesai! File akan segera dibuka."}
                  </MDTypography>
                </MDBox>
              )}

              {/* Informasi Setelah Download Selesai */}
              {downloadComplete && (
                <MDBox
                  mt={3}
                  p={2}
                  borderRadius={2}
                  bgcolor="info.lighter"
                  border="1px solid"
                  borderColor="info.main"
                >
                  <MDBox display="flex" alignItems="flex-start" gap={1}>
                    <Icon color="info" sx={{ mt: 0.5 }}>
                      folder_open
                    </Icon>
                    <MDBox flex={1}>
                      <MDTypography
                        variant="body2"
                        color="text"
                        fontWeight="bold"
                        mb={1}
                      >
                        File Berhasil Diunduh!
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        color="text.secondary"
                        mb={1}
                      >
                        File <strong>Rote-Smart-Service.apk</strong> telah
                        tersimpan di folder Downloads Anda.
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        color="text.secondary"
                        component="div"
                      >
                        <strong>Cara membuka folder Downloads:</strong>
                        <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
                          <li>
                            <strong>Windows:</strong> Tekan{" "}
                            <code>Ctrl + J</code> atau buka folder Downloads
                            dari File Explorer
                          </li>
                          <li>
                            <strong>Mac:</strong> Buka Finder dan pilih folder
                            Downloads
                          </li>
                          <li>
                            <strong>Android:</strong> Buka aplikasi File Manager
                            dan cari di folder Downloads
                          </li>
                        </ul>
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
              )}
            </MDBox>

            {/* Informasi Aplikasi */}
            <MDBox mb={4}>
              <MDTypography variant="h6" color="text" fontWeight="bold" mb={2}>
                Tentang Aplikasi
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" paragraph>
                Rote Smart Service adalah aplikasi mobile resmi yang
                dikembangkan oleh Pemerintah Kabupaten Rote Ndao untuk
                memudahkan akses layanan publik dan informasi pemerintahan.
              </MDTypography>
              <MDBox display="flex" alignItems="center" gap={1} mt={2}>
                <PhoneAndroidIcon color="primary" />
                <MDTypography variant="body2" color="text.secondary">
                  Platform: Android
                </MDTypography>
              </MDBox>
            </MDBox>

            <Divider sx={{ my: 3 }} />

            {/* Langkah-langkah Instalasi */}
            <MDBox mb={4}>
              <MDTypography variant="h6" color="text" fontWeight="bold" mb={3}>
                Langkah-langkah Instalasi
              </MDTypography>
              <List>
                {langkahInstalasi.map((langkah, index) => (
                  <div key={langkah.step}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          width={32}
                          height={32}
                          borderRadius="50%"
                          bgcolor="primary.main"
                          color="white"
                        >
                          <MDTypography variant="body2" fontWeight="bold">
                            {langkah.step}
                          </MDTypography>
                        </MDBox>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <MDTypography
                            variant="h6"
                            color="text"
                            fontWeight="medium"
                          >
                            {langkah.title}
                          </MDTypography>
                        }
                        secondary={
                          <MDTypography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {langkah.description}
                          </MDTypography>
                        }
                      />
                    </ListItem>
                    {index < langkahInstalasi.length - 1 && (
                      <Divider variant="inset" component="li" sx={{ ml: 5 }} />
                    )}
                  </div>
                ))}
              </List>
            </MDBox>

            {/* Catatan Penting */}
            <MDBox
              p={2}
              borderRadius={2}
              bgcolor="info.lighter"
              border="1px solid"
              borderColor="info.main"
            >
              <MDBox display="flex" alignItems="flex-start" gap={1}>
                <Icon color="info" sx={{ mt: 0.5 }}>
                  info
                </Icon>
                <MDBox>
                  <MDTypography
                    variant="body2"
                    color="text"
                    fontWeight="medium"
                    mb={1}
                  >
                    Catatan Penting:
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    color="text.secondary"
                    component="div"
                  >
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>
                        Pastikan ponsel Anda memiliki ruang penyimpanan yang
                        cukup untuk menginstal aplikasi.
                      </li>
                      <li>
                        Setelah instalasi, Anda dapat menghapus file APK dari
                        ponsel untuk menghemat ruang penyimpanan.
                      </li>
                      <li>
                        Aplikasi ini hanya tersedia untuk perangkat Android.
                      </li>
                    </ul>
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          </CardContent>
        </Card>
      </Container>
    </ThemeProvider>
  );
}

export default DownloadRSS;
